import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Gift } from "lucide-react";

export default function Register() {
  const { register, lang } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const [form, setForm] = useState({ name: "", email: "", password: "", user_type: refCode ? "teacher" : "candidate" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (refCode) payload.ref_code = refCode.toUpperCase();
      const u = await register(payload);
      toast.success(lang === "fr" ? "Compte créé !" : "Account created!");
      nav(u.user_type === "company" ? "/company" : u.user_type === "teacher" ? "/teacher" : "/profile");
    } catch (err) {
      toast.error(err.response?.data?.detail || t(lang, "register_fail"));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/map';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Layout>
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="rounded-3xl bg-white border border-slate-200 p-8 shadow-sm">
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#1E3A8A]">{t(lang, "register_title")}</h1>
            <p className="text-slate-600 mt-2 text-sm">{t(lang, "register_sub")}</p>

            {refCode && (
              <div data-testid="referral-banner" className="mt-4 rounded-2xl bg-gradient-to-br from-[#FFF7ED] to-[#FFEDD5] border border-[#F97316]/30 p-4 flex items-start gap-3">
                <Gift className="w-5 h-5 text-[#F97316] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-[#C2410C]">
                    {lang === "fr" ? "Invitation parrainage" : "Referral invite"}
                  </p>
                  <p className="text-xs text-slate-700 mt-0.5">
                    {lang === "fr"
                      ? `Vous êtes invité·e par une entreprise via le code ${refCode.toUpperCase()}. Créez un compte enseignant, puis votre première classe — l'entreprise recevra automatiquement 30 jours de Boost.`
                      : `You were invited by a company via code ${refCode.toUpperCase()}. Sign up as a teacher, create your first class — the company will automatically receive 30 days of Boost.`}
                  </p>
                </div>
              </div>
            )}

            <Button data-testid="google-signup-btn" onClick={googleLogin} variant="outline" className="w-full mt-6 h-11 rounded-full border-slate-300">
              {t(lang, "continue_google")}
            </Button>

            <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
              <div className="h-px bg-slate-200 flex-1" /> {t(lang, "or")} <div className="h-px bg-slate-200 flex-1" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>{t(lang, "account_type")}</Label>
                <RadioGroup value={form.user_type} onValueChange={(v) => setForm({ ...form, user_type: v })} className="flex gap-4 mt-2">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="candidate" id="rc" data-testid="type-candidate" />
                    <Label htmlFor="rc" className="cursor-pointer">{t(lang, "candidate")}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="company" id="rcp" data-testid="type-company" />
                    <Label htmlFor="rcp" className="cursor-pointer">{t(lang, "company")}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="teacher" id="rt" data-testid="type-teacher" />
                    <Label htmlFor="rt" className="cursor-pointer">{lang === "fr" ? "Enseignant" : "Teacher"}</Label>
                  </div>
                </RadioGroup>
              </div>
              <div>
                <Label htmlFor="name">{t(lang, "name")}</Label>
                <Input data-testid="reg-name" id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="email">{t(lang, "email")}</Label>
                <Input data-testid="reg-email" id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="password">{t(lang, "password_label")}</Label>
                <Input data-testid="reg-password" id="password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" />
              </div>
              <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full h-11 bg-[#F97316] hover:bg-[#EA580C] rounded-full">
                {loading ? "..." : t(lang, "cta_signup")}
              </Button>
            </form>

            <p className="text-center text-sm text-slate-600 mt-6">
              {t(lang, "have_account")} <Link to="/login" data-testid="link-login" className="text-[#1E3A8A] font-semibold hover:underline">{t(lang, "cta_login")}</Link>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
